/**
 * Tiny markdown renderer used by both assistants. Supports paragraphs,
 * **bold**, *italic*, `code`, [text](url) links, and `- bullet` lists.
 *
 * Extracted from assistant-chat-panel.tsx so the new Chat IA page can
 * reuse the exact same rendering without duplicating the regex.
 */
import { useMemo, type ReactNode } from "react";
import { Link } from "wouter";

const INTERNAL_PREFIXES = ["/app/projects", "/app/mail", "/app/today", "/app/chat"];

function isSafeUrl(url: string): boolean {
  if (url.startsWith("/app/")) return true;
  if (url.startsWith("/")) return true;
  return /^https?:\/\//i.test(url);
}
function isInternalLink(url: string): boolean {
  return INTERNAL_PREFIXES.some((p) => url === p || url.startsWith(p));
}

function renderInline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\[[^\]]+\]\([^\s)]+\)|\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) out.push(text.slice(last, match.index));
    const tok = match[0];
    if (tok.startsWith("[")) {
      const linkMatch = /^\[([^\]]+)\]\(([^\s)]+)\)$/.exec(tok);
      if (linkMatch) {
        const [, label, url] = linkMatch;
        if (!isSafeUrl(url)) {
          out.push(label);
        } else if (isInternalLink(url)) {
          out.push(
            <Link
              key={key++}
              href={url}
              className="text-primary underline underline-offset-2 hover:opacity-80"
            >
              {label}
            </Link>,
          );
        } else {
          out.push(
            <a
              key={key++}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline underline-offset-2 hover:opacity-80"
            >
              {label}
            </a>,
          );
        }
      } else {
        out.push(tok);
      }
    } else if (tok.startsWith("**"))
      out.push(<strong key={key++}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith("`"))
      out.push(
        <code
          key={key++}
          className="px-1 py-0.5 rounded bg-secondary text-[0.85em]"
        >
          {tok.slice(1, -1)}
        </code>,
      );
    else out.push(<em key={key++}>{tok.slice(1, -1)}</em>);
    last = match.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function MarkdownLite({ text }: { text: string }) {
  const blocks = useMemo(() => {
    const lines = text.split(/\r?\n/);
    const result: ReactNode[] = [];
    let bullets: string[] = [];
    const flushBullets = () => {
      if (bullets.length) {
        result.push(
          <ul
            key={`ul-${result.length}`}
            className="list-disc pl-5 space-y-0.5 my-1"
          >
            {bullets.map((b, i) => (
              <li key={i}>{renderInline(b)}</li>
            ))}
          </ul>,
        );
        bullets = [];
      }
    };
    for (const raw of lines) {
      const line = raw.trimEnd();
      if (/^\s*-\s+/.test(line)) {
        bullets.push(line.replace(/^\s*-\s+/, ""));
      } else if (line === "") {
        flushBullets();
      } else if (/^#{1,6}\s+/.test(line)) {
        flushBullets();
        const m = /^(#{1,6})\s+(.*)$/.exec(line)!;
        const level = m[1].length;
        const sizes: Record<number, string> = {
          1: "text-lg font-bold mt-2",
          2: "text-base font-bold mt-2",
          3: "text-sm font-bold mt-1",
          4: "text-sm font-semibold",
          5: "text-sm font-semibold",
          6: "text-sm font-medium",
        };
        result.push(
          <p key={`h-${result.length}`} className={sizes[level] ?? "font-bold"}>
            {renderInline(m[2])}
          </p>,
        );
      } else {
        flushBullets();
        result.push(
          <p key={`p-${result.length}`} className="leading-relaxed">
            {renderInline(line)}
          </p>,
        );
      }
    }
    flushBullets();
    return result;
  }, [text]);

  return <div className="space-y-1 text-sm">{blocks}</div>;
}
